import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {useQueryClient} from '@github-ui/react-query'
import {CopyIcon, DuplicateIcon, XIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'

import {copyProjectOwnersQueryOptions} from '../../queries/copy-project-owners'
import {MENU_LABELS} from './menu-labels'
import type {Project} from './types'

interface ProjectRowActionMenuProps {
  project: Project
  onCopyProject: (project: Project) => void
  onCopyAsTemplate: (project: Project) => void
  onUnlinkProject: (project: Project) => void
}

/**
 * Kebab action menu for a single project row in the repo projects index. Mirrors
 * the repo-context branch of `Memex::ProjectList::ActionMenuComponent`.
 */
export function ProjectRowActionMenu({
  project,
  onCopyProject,
  onCopyAsTemplate,
  onUnlinkProject,
}: ProjectRowActionMenuProps) {
  const showCopy = project.viewerCanCopy
  const showCopyAsTemplate = project.viewerCanCopyAsTemplate
  const showUnlink = project.viewerCanWrite
  const showDivider = (showCopy || showCopyAsTemplate) && showUnlink

  const queryClient = useQueryClient()
  const prefetchOwners = (copyAsTemplate: boolean) => {
    void queryClient.prefetchQuery(copyProjectOwnersQueryOptions({projectId: project.id, copyAsTemplate}))
  }

  return (
    <ListItemActionBar
      label={`Actions for ${project.title}`}
      staticMenuActions={[
        ...(showCopy
          ? [
              {
                key: 'copy',
                render: () => (
                  <ActionList.Item
                    onSelect={() => onCopyProject(project)}
                    onMouseEnter={() => prefetchOwners(false)}
                    onFocus={() => prefetchOwners(false)}
                  >
                    <ActionList.LeadingVisual>
                      <CopyIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.copy}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showCopyAsTemplate
          ? [
              {
                key: 'copy-as-template',
                render: () => (
                  <ActionList.Item
                    onSelect={() => onCopyAsTemplate(project)}
                    onMouseEnter={() => prefetchOwners(true)}
                    onFocus={() => prefetchOwners(true)}
                  >
                    <ActionList.LeadingVisual>
                      <DuplicateIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.copyAsTemplate}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
        ...(showDivider
          ? [
              {
                key: 'divider',
                render: () => <ActionList.Divider />,
              },
            ]
          : []),
        ...(showUnlink
          ? [
              {
                key: 'unlink',
                render: () => (
                  <ActionList.Item variant="danger" onSelect={() => onUnlinkProject(project)}>
                    <ActionList.LeadingVisual>
                      <XIcon aria-hidden="true" />
                    </ActionList.LeadingVisual>
                    {MENU_LABELS.unlink}
                  </ActionList.Item>
                ),
              },
            ]
          : []),
      ]}
    />
  )
}
